import { useState, useEffect, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { supabase } from '@/lib/supabaseClient';
import AppLayout from '../components/AppLayout';
import StepHeader from './components/StepHeader';
import RoleSelect from './components/RoleSelect';
import TermsStep from './components/TermsStep';
import BasicInfoStep from './components/BasicInfoStep';
import OtpStep from './components/OtpStep';
import SuccessStep from './components/SuccessStep';

export type RegisterStep = 'role' | 'terms' | 'info' | 'otp' | 'success';

type AddressValue = { zonecode: string; roadAddress: string; jibunAddress: string; detailAddress: string };

const TOTAL_STEPS = 4;

function getStepIndex(step: RegisterStep): number {
  if (step === 'role') return 0;
  if (step === 'terms') return 1;
  if (step === 'info') return 2;
  if (step === 'otp') return 3;
  return 0;
}

function getNextStep(current: RegisterStep): RegisterStep {
  const map: Record<RegisterStep, RegisterStep> = {
    'role': 'terms',
    'terms': 'info',
    'info': 'otp',
    'otp': 'success',
    'success': 'success',
  };
  return map[current];
}

function getPrevStep(current: RegisterStep): RegisterStep | null {
  const map: Record<RegisterStep, RegisterStep | null> = {
    'role': null,
    'terms': 'role',
    'info': 'terms',
    'otp': 'info',
    'success': 'otp',
  };
  return map[current] ?? null;
}

export default function AppRegisterPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const state = location.state as {
    phone?: string;
    preselectedRole?: string;
    kakaoUserId?: string;
    kakaoName?: string;
    kakaoEmail?: string;
    kakaoPhone?: string;
  } | null;

  const passedPhone = state?.phone || state?.kakaoPhone || '';
  const preselectedRole = state?.preselectedRole;
  const kakaoUserId = state?.kakaoUserId;

  const getInitialStep = (): RegisterStep => {
    if (kakaoUserId) return preselectedRole ? 'terms' : 'role';
    if (preselectedRole) return 'terms';
    return 'role';
  };

  const [step, setStep] = useState<RegisterStep>(getInitialStep);
  const [role, setRole] = useState<'patient' | 'caregiver' | ''>(preselectedRole as 'patient' | 'caregiver' || '');

  // Basic info state
  const [basicInfo, setBasicInfo] = useState({
    name: '',
    phone: passedPhone,
    gender: '' as '남' | '여' | '',
    birthDate: '',
    address: { zonecode: '', roadAddress: '', jibunAddress: '', detailAddress: '' } as AddressValue,
    avatarUrl: '',
  });

  // OTP
  const [otp, setOtp] = useState('');
  const [countdown, setCountdown] = useState(0);
  const [showMockNotice, setShowMockNotice] = useState(false);
  const [mockCode, setMockCode] = useState('');
  const [pendingUserId, setPendingUserId] = useState<string | null>(null);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [isAddingRole, setIsAddingRole] = useState(false);

  useEffect(() => {
    if (countdown <= 0) return;
    const timer = setTimeout(() => setCountdown((c) => c - 1), 1000);
    return () => clearTimeout(timer);
  }, [countdown]);

  const handleBack = useCallback(() => {
    const prev = getPrevStep(step);
    if (prev) {
      setStep(prev);
    } else {
      navigate('/app');
    }
  }, [step, navigate]);

  const handleRoleSelect = useCallback((selected: 'patient' | 'caregiver') => {
    setRole(selected);
    setStep('terms');
  }, []);

  const handleTermsSubmit = useCallback(() => {
    setStep('info');
  }, []);

  const handleBasicInfoSubmit = useCallback(() => {
    setStep('otp');
    sendOtp();
  }, []);

  async function sendOtp() {
    setError('');
    setShowMockNotice(false);
    setMockCode('');
    const phone = basicInfo.phone;
    if (!phone || phone.length < 10) {
      setError('휴대폰 번호를 정확히 입력해주세요.');
      return;
    }
    setLoading(true);

    try {
      const { data, error: fnError } = await supabase.functions.invoke('send-otp-kr', {
        body: { phone: phone.replace(/\D/g, '') },
      });

      if (fnError || (data && data.error)) {
        setLoading(false);
        setError(data?.error || '인증번호 발송 중 오류가 발생했습니다.');
        return;
      }

      setLoading(false);
      if (data && data.mock) {
        setShowMockNotice(true);
        if (data.code) setMockCode(data.code);
      }
      setCountdown(300);
    } catch {
      setLoading(false);
      setError('네트워크 오류가 발생했습니다.');
    }
  }

  async function handleVerifyOtp() {
    setError('');
    if (!otp || otp.length !== 6) {
      setError('인증번호 6자리를 입력해주세요.');
      return;
    }
    setLoading(true);

    try {
      const { data, error: fnError } = await supabase.functions.invoke('verify-otp', {
        body: { phone: basicInfo.phone.replace(/\D/g, ''), code: otp },
      });

      setLoading(false);
      if (fnError || (data && data.error)) {
        setError(data?.error || '인증 확인 중 오류가 발생했습니다.');
        return;
      }

      if (data && data.success) {
        setPendingUserId(data.userId);
        await createProfile(data.userId, data.roles || []);
        return;
      }
      setError('인증에 실패했습니다.');
    } catch {
      setLoading(false);
      setError('네트워크 오류가 발생했습니다.');
    }
  }

  async function createProfile(userId: string, existingRoles: string[] | null) {
    const cleanPhone = basicInfo.phone.replace(/\D/g, '');
    const formattedAddress = `(${basicInfo.address.zonecode}) ${basicInfo.address.roadAddress}${basicInfo.address.detailAddress ? ', ' + basicInfo.address.detailAddress : ''}`;

    const { data: existingProfile } = await supabase
      .from('profiles')
      .select('id, roles, role')
      .eq('phone', cleanPhone)
      .maybeSingle();

    if (existingProfile) {
      const currentRoles: string[] = existingProfile.roles?.length
        ? existingProfile.roles
        : (existingProfile.role ? [existingProfile.role] : []);
      if (currentRoles.includes(role)) {
        setLoading(false);
        setError(`이미 ${role === 'patient' ? '환자/보호자' : '간병인'}로 등록되어 있습니다.`);
        return;
      }

      const newRoles = [...currentRoles, role];
      let updateData: Record<string, unknown> = { roles: newRoles };

      if (role === 'caregiver') {
        const today = new Date();
        const dateStr = `${today.getFullYear()}${String(today.getMonth() + 1).padStart(2, '0')}${String(today.getDate()).padStart(2, '0')}`;
        const { data: maxNum } = await supabase
          .from('profiles')
          .select('caregiver_number')
          .ilike('caregiver_number', `CARE-${dateStr}-%`)
          .order('caregiver_number', { ascending: false })
          .limit(1)
          .maybeSingle();
        let seq = 1;
        if (maxNum?.caregiver_number) {
          const parts = maxNum.caregiver_number.split('-');
          const lastSeq = parseInt(parts[2], 10);
          if (!isNaN(lastSeq)) seq = lastSeq + 1;
        }
        updateData.caregiver_number = `CARE-${dateStr}-${String(seq).padStart(3, '0')}`;
      }

      const { error: updateErr } = await supabase
        .from('profiles')
        .update(updateData)
        .eq('id', existingProfile.id);

      if (updateErr) { setLoading(false); setError(updateErr.message); return; }

      setIsAddingRole(true);
      setTimeout(() => {
        navigate('/app', { replace: true });
      }, 1500);
      return;
    }

    // 신규 등록
    let caregiverNumber: string | null = null;
    if (role === 'caregiver') {
      const today = new Date();
      const dateStr = `${today.getFullYear()}${String(today.getMonth() + 1).padStart(2, '0')}${String(today.getDate()).padStart(2, '0')}`;
      const { data: maxNum } = await supabase
        .from('profiles')
        .select('caregiver_number')
        .ilike('caregiver_number', `CARE-${dateStr}-%`)
        .order('caregiver_number', { ascending: false })
        .limit(1)
        .maybeSingle();
      let seq = 1;
      if (maxNum?.caregiver_number) {
        const parts = maxNum.caregiver_number.split('-');
        const lastSeq = parseInt(parts[2], 10);
        if (!isNaN(lastSeq)) seq = lastSeq + 1;
      }
      caregiverNumber = `CARE-${dateStr}-${String(seq).padStart(3, '0')}`;
    }

    const { error: insertErr } = await supabase.from('profiles').insert({
      id: userId,
      role,
      roles: [role],
      name: basicInfo.name,
      phone: cleanPhone,
      gender: basicInfo.gender,
      birth_date: basicInfo.birthDate,
      address: formattedAddress,
      caregiver_number: caregiverNumber,
      avatar_url: basicInfo.avatarUrl || null,
    });

    if (insertErr) {
      setLoading(false);
      setError('등록 중 오류가 발생했습니다. 다시 시도해주세요.');
      return;
    }

    localStorage.setItem('kc_demo_login', JSON.stringify({
      phone: cleanPhone,
      userId,
      roles: [role],
      timestamp: Date.now(),
    }));
    localStorage.setItem('kc_demo_user_id', userId);

    setStep('success');
  }

  const stepIndex = getStepIndex(step);
  const subtitle = step === 'otp' ? `${basicInfo.phone}로 전송된 인증번호를 입력해주세요` : undefined;

  return (
    <AppLayout>
      <div className="min-h-screen px-6 pt-12 pb-16 bg-white">
        {isAddingRole && (
          <div className="mb-6 p-4 rounded-xl bg-green-50 text-center">
            <i className="ri-checkbox-circle-line text-green-500 text-2xl mb-2" />
            <p className="text-sm font-bold text-green-700">
              {role === 'caregiver' ? '간병인' : '환자/보호자'} 역할이 추가되었습니다!
            </p>
          </div>
        )}

        {step !== 'success' && (
          <StepHeader
            step={step}
            stepIndex={stepIndex}
            totalSteps={TOTAL_STEPS}
            onBack={handleBack}
            subtitle={subtitle}
          />
        )}

        {step === 'role' && (
          <RoleSelect selectedRole={role} onSelect={handleRoleSelect} />
        )}

        {step === 'terms' && <TermsStep onSubmit={handleTermsSubmit} onBack={handleBack} />}

        {step === 'info' && (
          <BasicInfoStep
            data={basicInfo}
            onChange={(d) => setBasicInfo((prev) => ({ ...prev, ...d }))}
            onSubmit={handleBasicInfoSubmit}
            onBack={handleBack}
            role={role as 'patient' | 'caregiver'}
            onChangeRole={() => setStep('role')}
          />
        )}

        {step === 'otp' && (
          <OtpStep
            phone={basicInfo.phone}
            otp={otp}
            onOtpChange={setOtp}
            countdown={countdown}
            showMockNotice={showMockNotice}
            mockCode={mockCode}
            loading={loading}
            onVerify={handleVerifyOtp}
            onResend={sendOtp}
            onBack={handleBack}
          />
        )}

        {step === 'success' && <SuccessStep role={role as 'patient' | 'caregiver'} />}

        {error && (
          <div className="mt-4 p-3 rounded-xl bg-red-50 text-xs text-red-500 text-center">{error}</div>
        )}
      </div>
    </AppLayout>
  );
}